import React, { useEffect, useMemo, useState } from 'react';
import {
  LuCake,
  LuSparkles,
  LuCakeSlice,
  LuCookie,
  LuCupSoda,
  LuLayers,
  LuCrown,
  LuGift,
  LuStar
} from 'react-icons/lu';
import CakeDesign from '../forms/CakeDesign';

const cakeTypes = [
  {
    value: 'Butter Cake',
    title: 'Butter Cake',
    subtitle: 'Light, buttery layers',
    icon: LuCakeSlice
  },
  {
    value: 'Chocolate Cake',
    title: 'Chocolate Cake',
    subtitle: 'Rich cocoa comfort',
    icon: LuCookie
  },
  {
    value: 'Cupcake (with cream)',
    title: 'Cupcake (cream swirls)',
    subtitle: 'Whipped peaks included',
    icon: LuCupSoda
  },
  {
    value: 'Cupcake (without cream)',
    title: 'Cupcake (no cream)',
    subtitle: 'Minimal & elegant',
    icon: LuStar
  },
  {
    value: 'Wedding Cake',
    title: 'Wedding Cake',
    subtitle: 'Tiered & timeless',
    icon: LuCrown,
    badge: 'Premium'
  },
  {
    value: 'Fondant Cake',
    title: 'Fondant Cake',
    subtitle: 'Sculpted artistry',
    icon: LuLayers,
    badge: 'Premium'
  },
  {
    value: 'Birthday Cake',
    title: 'Birthday Cake',
    subtitle: 'Party favorite',
    icon: LuGift,
    badge: 'Popular',
    badgeColor: 'bg-amber-500'
  },
  {
    value: 'Custom Order',
    title: 'Custom Order',
    subtitle: 'Describe your wildest idea',
    icon: LuSparkles,
    isCustom: true,
    badge: 'Custom'
  }
];

const OrderSection = () => {
  const [selectedCakeType, setSelectedCakeType] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotionPreference = (event) => setPrefersReducedMotion(event.matches);

    setPrefersReducedMotion(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateMotionPreference);
    } else {
      mediaQuery.addListener(updateMotionPreference);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateMotionPreference);
      } else {
        mediaQuery.removeListener(updateMotionPreference);
      }
    };
  }, []);

  const motionEnabled = !prefersReducedMotion;

  const getPanelClasses = (isVisible) => {
    const base = 'transition-all duration-500 ease-out w-full';
    if (isVisible) {
      return `${base} opacity-100 translate-x-0 rotate-0 scale-100 relative`;
    }
    const hidden = `${base} opacity-0 pointer-events-none absolute inset-0`;
    return motionEnabled ? `${hidden} translate-x-8 rotate-3 scale-95` : hidden;
  };

  const handleSelect = (type) => {
    if (isAnimating) return;
    setSelectedCakeType(type);
    setIsAnimating(true);
    setShowForm(true);
    setTimeout(() => setIsAnimating(false), 520);
  };

  const handleBack = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setShowForm(false);
    setTimeout(() => setIsAnimating(false), 520);
  };

  const gridClasses = useMemo(() => getPanelClasses(!showForm), [showForm, motionEnabled]);
  const formClasses = useMemo(() => getPanelClasses(showForm), [showForm, motionEnabled]);

  return (
    <section id="order" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 scroll-mt-24">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-flex items-center justify-center gap-2 mb-3 text-pink-600 font-medium">
            <LuSparkles className="h-4 w-4" aria-hidden="true" />
            GET STARTED
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold font-serif text-gray-900 mb-4 flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
            <LuCake className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 text-pink-500 flex-shrink-0" aria-hidden="true" />
            Design Your Dream Cake
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Choose a cake style card to unlock the form and we'll bring your vision to life.
          </p>
        </div>

        <div className="relative rounded-3xl bg-white/90 shadow-xl ring-1 ring-rose-100 p-6 sm:p-10 overflow-hidden">
          <div className="absolute -top-20 -right-16 h-72 w-72 rounded-full bg-pink-200/40 blur-3xl" aria-hidden="true" />
          <div className="absolute bottom-10 left-0 h-40 w-40 rounded-full bg-amber-100/50 blur-3xl" aria-hidden="true" />
          <div className="relative min-h-0 md:min-h-[620px]">
            <div className={gridClasses} aria-hidden={showForm}>
              <div className="mb-8 text-center">
                <p className="text-sm uppercase tracking-[0.4em] text-pink-500">Choose a cake type to start</p>
                <p className="text-gray-500 mt-2">Tap a card and watch the form glide in with your selection</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {cakeTypes.map(({ value, title, subtitle, icon: Icon, badge, badgeColor, isCustom }) => {
                  const isSelected = selectedCakeType === value;
                  const badgeClasses = (() => {
                    if (!badge) return '';
                    if (badge === 'Premium' && !isCustom) return 'bg-purple-100 text-purple-700';
                    if (badge === 'Popular') return 'bg-amber-100 text-amber-700';
                    if (badge === 'Custom' && isCustom) return 'bg-white/30 text-white';
                    return badgeColor || 'bg-white/20 text-white';
                  })();
                  return (
                    <button
                      key={value}
                      type="button"
                      disabled={isAnimating}
                      onClick={() => handleSelect(value)}
                      className={`group relative flex h-full flex-col items-start justify-between rounded-2xl border border-white/60 bg-white/90 p-5 text-left shadow-lg transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 ${
                        isSelected ? 'ring-2 ring-pink-500' : 'ring-1 ring-transparent'
                      } ${isCustom ? 'md:col-span-2 bg-gradient-to-r from-pink-500 via-rose-500 to-orange-400 text-white lg:flex-row lg:items-center lg:gap-6' : ''}`}
                      aria-pressed={isSelected}
                    >
                      <div className={`flex items-center justify-center rounded-2xl ${
                        isCustom ? 'bg-white/20 text-white h-16 w-16' : 'bg-pink-50 text-pink-600 h-12 w-12'
                      }`}
                      >
                        <Icon className={`${isCustom ? 'h-8 w-8' : 'h-6 w-6'}`} aria-hidden="true" />
                      </div>
                      <div className="mt-4 lg:mt-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-lg font-semibold ${isCustom ? 'text-white' : 'text-gray-900'}`}>{title}</p>
                          {badge && (
                            <span className={`text-xs font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 ${badgeClasses}`}>
                              {badge}
                            </span>
                          )}
                        </div>
                        <p className={`text-sm mt-1 ${isCustom ? 'text-white/90' : 'text-gray-500'}`}>{subtitle}</p>
                      </div>
                      {isCustom && (
                        <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-1 text-sm font-semibold">
                          <LuSparkles className="h-4 w-4" aria-hidden="true" />
                          Go fully bespoke
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={formClasses} aria-hidden={!showForm}>
              {showForm && (
                <div className="flex flex-col gap-6">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <button
                      type="button"
                      onClick={handleBack}
                      disabled={isAnimating}
                      className="inline-flex items-center gap-2 text-pink-600 font-semibold hover:text-pink-700"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l-5 5 5 5" />
                      </svg>
                      Back to cake types
                    </button>
                    <span className="inline-flex items-center gap-2 rounded-full bg-pink-50 px-4 py-2 text-sm font-semibold text-pink-700">
                      <LuCake className="h-4 w-4" aria-hidden="true" />
                      {selectedCakeType}
                    </span>
                  </div>
                  <div className="bg-white rounded-2xl shadow-xl border border-pink-50">
                    <CakeDesign initialCakeType={selectedCakeType} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrderSection;
